import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Bell, Check, X, Clock, AlertTriangle, CalendarDays, ListTodo, CheckCheck, Trash2, Radar, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications?limit=20`, { headers: getAuthHeaders() }),
        axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeaders() })
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [getAuthHeaders, user]);

  // Poll for notifications every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/notifications/${notificationId}/dismiss`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const notif = notifications.find(n => n.id === notificationId);
      if (notif && !notif.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: getAuthHeaders() });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  // Clear all notifications
  const clearAll = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/notifications/clear-all`, {}, { headers: getAuthHeaders() });
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    
    if (notification.itemType === 'monitoring') {
      navigate('/deep-search');
    } else if (notification.itemType === 'abuse_log') {
      navigate('/admin');
    } else if (notification.itemType === 'private_event') {
      navigate('/get-it-done', { state: { openEventId: notification.itemId } });
    } else if (notification.itemType === 'task' && notification.listId) {
      navigate('/get-it-done', { state: { openListId: notification.listId, highlightItemId: notification.itemId } });
    }
  };

  // Get notification icon
  const getNotificationIcon = (type, itemType) => {
    if (itemType === 'monitoring' || type === 'monitoring_alert') return <Radar className="h-4 w-4 text-purple-500" />;
    if (type === 'monitoring_paused') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    if (type === 'security_alert' || itemType === 'abuse_log') return <AlertTriangle className="h-4 w-4 text-red-500" />;
    switch (type) {
      case 'reminder': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'overdue': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'today_highlight': return <CalendarDays className="h-4 w-4 text-amber-500" />;
      default: return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notifications-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-card border rounded-xl shadow-lg z-50 overflow-hidden" data-testid="notifications-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b bg-muted/30">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {notifications.length > 0 && (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={markAllAsRead}
                  disabled={loading || unreadCount === 0}
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Mark all read
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-destructive hover:text-destructive"
                  onClick={clearAll}
                  disabled={loading}
                  data-testid="clear-all-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs">You&apos;re all caught up!</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`flex items-start gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-muted/50 transition-colors ${
                    !notif.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                  data-testid={`notification-${notif.id}`}
                >
                  {/* Icon */}
                  <div className="shrink-0 mt-0.5">
                    {notif.itemType === 'task' ? (
                      <ListTodo className="h-4 w-4 text-emerald-500" />
                    ) : (
                      getNotificationIcon(notif.type, notif.itemType)
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${!notif.read ? 'font-medium' : ''}`}>
                      {notif.title}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {notif.message}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {!notif.read && (
                      <div className="h-2 w-2 rounded-full bg-blue-500" title="Unread" />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-50 hover:opacity-100"
                      onClick={(e) => dismissNotification(notif.id, e)}
                      title="Dismiss"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
